import { ApiErrorMessage } from '@/components/shared/api-error-message';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ApiError } from '@portfolio-manager/sdk';
import React from 'react';

export interface DataPanelItem<TRecord> {
  key: string;
  label: React.ReactNode;
  value: (record: TRecord) => React.ReactNode;
  className?: string;
}

interface DataPanelItemViewProps<TRecord> {
  item: DataPanelItem<TRecord>;
  record: TRecord | undefined;
  isPending?: boolean;
  emptyValue?: React.ReactNode;
}

function DataPanelItemView<TRecord>({
  item,
  record,
  isPending,
  emptyValue,
}: DataPanelItemViewProps<TRecord>): React.ReactElement {
  const value = record === undefined ? undefined : item.value(record);
  return (
    <div className={cn('flex flex-col gap-1', item.className)}>
      <dt className="text-sm text-muted-foreground">{item.label}</dt>
      <dd className="text-sm">
        {isPending || record === undefined ? (
          <Skeleton className="h-4 w-24" />
        ) : value === null || value === undefined || value === '' ? (
          <span className="text-muted-foreground">{emptyValue}</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

export interface DataPanelProps<TRecord> {
  items: DataPanelItem<TRecord>[];
  record: TRecord | undefined;
  isPending?: boolean;
  error?: ApiError | null;
  emptyValue?: React.ReactNode;
  maxItems?: number;
  className?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
}

export function DataPanel<TRecord>({
  items,
  record,
  isPending = false,
  error = null,
  emptyValue = '-',
  maxItems,
  showLessLabel,
  showMoreLabel,
  className,
}: DataPanelProps<TRecord>): React.ReactElement {
  const [showMore, setShowMore] = React.useState<boolean>(false);

  const previewItems: DataPanelItem<TRecord>[] = React.useMemo(() => {
    if (maxItems !== undefined) {
      return items.slice(0, maxItems);
    }
    return items;
  }, [items, maxItems]);

  const allowShowMore: boolean = React.useMemo(() => {
    return !!maxItems && maxItems < items.length;
  }, [items, maxItems]);

  return (
    <div className={cn('w-full', className)}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 border-t p-4 sm:grid-cols-2 lg:grid-cols-3">
        {(allowShowMore && showMore ? items : previewItems).map((item) => (
          <DataPanelItemView key={item.key} item={item} record={record} isPending={isPending} emptyValue={emptyValue} />
        ))}
      </dl>
      {allowShowMore && (
        <div className="flex justify-center">
          <Button variant="link" className="cursor-pointer" onClick={() => setShowMore((prev) => !prev)}>
            {showMore ? showLessLabel || 'Show less' : showMoreLabel || 'Show more'}
          </Button>
        </div>
      )}
      <ApiErrorMessage error={error} />
    </div>
  );
}
